import { Inject, Injectable, } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import i18n, { TOptions } from 'i18next';
// import Backend from 'i18next-xhr-backend'; // o i18next-http-backend
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class I18nService {
    // private static readonly DEFAULT_LANGUAGE = 'es';
    private static readonly DEFAULT_LANGUAGE = 'es';
    private language = new BehaviorSubject<string>(this.getDefaultLanguage());

    /**
     * Initializes the internationalization service.
     * @param http HttpClient used to load translation files.
     * @param environment The application's environment configuration.
     */
    constructor(
        private http: HttpClient,
        @Inject('environment') private environment: any,
    ) {
        i18n
            .init({
                lng: this.language.value,
                fallbackLng: I18nService.DEFAULT_LANGUAGE,
            });
    }
    /** Retrieves the current active language. */
    get currentLanguage(): string {
        return this.language.value;
    }

    /** Emits an event whenever the active language changes. */
    get languageChange$(): Observable<string> {
        return this.language.asObservable();
    }

    /** Detects and returns the default language based on browser settings. */
    private getDefaultLanguage(): string {
        return this.detectLanguage();
    }

    /** Devuelve los idiomas disponibles en i18n. */
    getAvailableLanguages(): string[] {
        return Array.from(i18n.languages);
    }

    /** Provides an array of available languages in the i18n instance. */
    changeLanguage(lang: string): void {
        i18n.changeLanguage(lang);
        this.language.next(lang);
    }

    /** Detects the browser's language setting. */
    detectLanguage(): string {
        return this.getLanguageCookie()?.substring(0, 2) || navigator.language?.substring(0, 2) || navigator.languages[0]?.substring(0, 2) || I18nService.DEFAULT_LANGUAGE;
    }

    /**
     * Returns a translation function for a specific namespace.
     * This is useful for accessing translations in an organized manner when multiple translation files are used.
     * @param namespace The namespace containing the translations.
     * @returns A function that takes a translation key and interpolation parameters and returns the corresponding translated string.
     */
    getTranslator(namespace: string) {
        return (key: string, interpolationParams?: TOptions): string | undefined => {
            return i18n.t(`${namespace}:${key}`, interpolationParams);
        };
    }

    /**
     * Adds a resource file to the i18n bundle for a specific language and namespace.
     * This allows for dynamic loading of translation files.
     *
     * @param namespace The namespace for the set of translations. This is also the name of the folder containing the translation files.
     * @param filePath The path to the translation file. The filepath complements the path of `/assets/translations/` with the file ending with the language used and required format.
     * @param lang? The language for which the file is loaded. Defaults to the current language.
     * @returns A promise that resolves when the load is successful or rejects in case of error.
     */
    async addFileToResourceBundle(namespace: string, filePath: string, lang: string = this.currentLanguage): Promise<void> {
        return new Promise((resolve, reject) => {
            this.http.get(`assets/translations/${namespace}/${filePath}.${lang ?? this.getDefaultLanguage()}.json`, { responseType: 'json' }).subscribe(
                {
                    next(translation: any) {
                        i18n.addResourceBundle(lang, namespace, translation);
                        resolve();
                    },
                    error(err) {
                        console.error(`Could not load translation file: ${filePath}`, err);
                        reject(err);
                    }

                }
            );
        });
    }

    /**
     * Adds a set of resources (translations) to i18n for a specific language and namespace.
     * Useful for adding translations that are already in object format.
     * @param lang The language for which the resource is added.
     * @param namespace The namespace for the set of translations.
     * @param resource The object containing the translations.
     */
    addResourceBundle(lang: string, namespace: string, resource: object): void {
        i18n.addResourceBundle(lang, namespace, resource);
    }

    /**
     * Checks if a specific language and namespace have been loaded into i18n.
     * @param lang The language to check.
     * @param namespace The namespace to check.
     * @returns True if the language and namespace are loaded, false otherwise.
     */
    isLanguageLoaded(lang: string, namespace: string): boolean {
        return i18n.hasResourceBundle(lang, namespace);
    }

    /**
     * Removes a resource bundle for a specific language and namespace.
     * @param lang The language for which to remove the resource bundle.
     * @param namespace The namespace of the resource bundle to be removed.
     */
    removeResourceBoubdle(lang: string, namespace: string): void {
        i18n.removeResourceBundle(lang, namespace);
    }

    /**
     * Translates a key using the current language and provided interpolation parameters.
     * @param key The key to translate.
     * @param interpolationParams Optional interpolation parameters for the translation.
     * @returns The translated string.
     */
    public translate(key: string, interpolationParams?: TOptions): string {
        return i18n.t(key, interpolationParams);
    }

    /**
     * Returns the plural form of a key based on a count and optional parameters.
     * @param key The key to be pluralized.
     * @param count The count used for pluralization.
     * @param params Optional additional parameters.
     * @returns The pluralized string.
     */
    getPluralForm(key: string, count: number, params?: object): string {
        return i18n.t(key, { count, ...params });
    }

    formatDate(date: Date, format: string, lang: string): string {
        // Implementación de formateo de fecha
        return '';
    }

    formatNumber(number: number, format: string, lang: string): string {
        // Implementación de formateo de número
        return '';
    }

    /**
     * Retrieves the cookie 'wp_lang' value
     * This cookie stores the preferred user language
     * @returns {string | null} The value of the stored lang in the cookie 'wp_lang'
     * or null if the cookie if it doesn´t exist
     */
    getLanguageCookie(): string | null {
        const name = 'wp_lang=';
        const decodedCookie = decodeURIComponent(document.cookie);
        const ca = decodedCookie.split(';');
        for (let i = 0; i < ca.length; i++) {
            let c = ca[i];
            while (c.charAt(0) === ' ') {
                c = c.substring(1);
            }
            if (c.indexOf(name) === 0) {
                return c.substring(name.length, c.length);
            }
        }
        return null;
    }
}
